'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import type { FormField } from '@/types/site'

interface ContactFormProps {
  siteId: string
  /**
   * Base URL for API calls. Defaults to '' (relative URL, works in editor preview).
   * At compile time, the absolute URL (e.g., https://siteble.ai) is
   * injected so that published pages on business.siteble.ai POST to the correct origin.
   */
  apiBaseUrl?: string
}

type FormState = 'idle' | 'sending' | 'sent' | 'error'

const DEFAULT_FIELDS: FormField[] = [
  { id: 'name', label: 'שם', type: 'text', required: true },
  { id: 'phone', label: 'טלפון', type: 'tel', required: true },
  { id: 'email', label: 'אימייל', type: 'email', required: false },
  { id: 'message', label: 'הודעה', type: 'textarea', required: true },
]

export function ContactForm({ siteId, apiBaseUrl = '' }: ContactFormProps) {
  const [formState, setFormState] = useState<FormState>('idle')
  const [fields, setFields] = useState<FormField[]>(DEFAULT_FIELDS)

  useEffect(() => {
    if (!siteId) return
    fetch(`${apiBaseUrl}/api/forms/config?siteId=${siteId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.fields?.length) setFields(data.fields)
      })
      .catch(() => {/* use defaults */})
  }, [siteId, apiBaseUrl])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setFormState('sending')

    const formData = new FormData(e.currentTarget)

    // Honeypot check
    if (formData.get('website')) {
      setFormState('sent')
      return
    }

    // Collect all field values by id
    const fieldValues: Record<string, string> = {}
    for (const field of fields) {
      const value = formData.get(`field-${field.id}`)
      if (typeof value === 'string' && value) {
        fieldValues[field.id] = value
      }
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          fields: fieldValues,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to submit form')
      }

      setFormState('sent')
    } catch {
      setFormState('error')
    }
  }

  if (formState === 'sent') {
    return (
      <div dir="rtl" className="space-y-4 max-w-lg mx-auto text-center py-8">
        <p className="text-lg font-medium text-green-600">
          תודה! נחזור אליך בהקדם
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      dir="rtl"
      className="space-y-4 max-w-lg mx-auto"
      data-contact-form
    >
      {/* Honeypot field - hidden from real users */}
      <input
        type="text"
        name="website"
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {fields.map((field) => (
        <div key={field.id} className="space-y-2">
          <Label htmlFor={`contact-${field.id}`}>
            {field.label}
            {field.required && <span className="text-destructive mr-1">*</span>}
          </Label>
          {field.type === 'textarea' ? (
            <Textarea
              id={`contact-${field.id}`}
              name={`field-${field.id}`}
              rows={4}
              placeholder={field.placeholder}
              required={field.required}
              aria-required={field.required}
              disabled={formState === 'sending'}
            />
          ) : field.type === 'select' && field.options ? (
            <select
              id={`contact-${field.id}`}
              name={`field-${field.id}`}
              required={field.required}
              aria-required={field.required}
              disabled={formState === 'sending'}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">{field.placeholder || `בחר ${field.label}`}</option>
              {field.options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : (
            <Input
              id={`contact-${field.id}`}
              name={`field-${field.id}`}
              type={field.type}
              placeholder={field.placeholder}
              dir={field.type === 'email' || field.type === 'tel' ? 'ltr' : undefined}
              required={field.required}
              aria-required={field.required}
              disabled={formState === 'sending'}
            />
          )}
        </div>
      ))}

      {formState === 'error' && (
        <p className="text-sm text-destructive">
          שגיאה בשליחת הטופס. נסה שוב.
        </p>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={formState === 'sending'}
      >
        {formState === 'sending' ? 'שולח...' : 'שלח'}
      </Button>
    </form>
  )
}
